import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Lock,
  ShieldCheck,
  Truck,
  Check,
  Loader2,
  ChevronLeft,
  Minus,
  Plus,
  ShoppingBag,
  Banknote,
  CreditCard,
  TicketPercent,
  X,
  MessageSquareText,
  BadgeCheck,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import { API, PRODUCT, fmt, IMAGES, comboUnitPrice, COD_FEE } from "../constants/site";
import { celebrateOrder } from "../utils/celebrate";
import { STATES, CITY_MAP } from "../constants/india";
import { useCart } from "../context/CartContext";
import Logo from "../components/Logo";

const initialForm = {
  email: "",
  full_name: "",
  phone: "",
  address1: "",
  address2: "",
  state: "",
  city: "",
  zip: "",
};

const inputCls =
  "w-full rounded-xl border border-charcoal-900/15 bg-white px-4 py-3.5 text-base sm:text-sm text-charcoal-900 placeholder:text-charcoal-500/50 transition-colors focus:border-sage-500 focus:outline-none focus:ring-2 focus:ring-sage-500/20";
const selectCls = `${inputCls} appearance-none bg-white`;

const loadRazorpayScript = () =>
  new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => reject(new Error("Could not load Razorpay checkout"));
    document.body.appendChild(s);
  });

// Pay Online stays on; the prepaid DISCOUNT offer is switched off (set SHOW_COUPON true to bring it back)
const SHOW_PREPAID = true;
const SHOW_COUPON = false;

export default function CheckoutPage() {
  const { items, subtotal, setQty, clear, count } = useCart();
  const [form, setForm] = useState(initialForm);
  const [payMethod, setPayMethod] = useState("cod");
  const [payStatus, setPayStatus] = useState({ razorpay_enabled: false });
  const [pinHint, setPinHint] = useState("");
  const [pinDetected, setPinDetected] = useState(null);
  const [otpStatus, setOtpStatus] = useState({ otp_enabled: false });
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpBusy, setOtpBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [couponInput, setCouponInput] = useState("");
  const [couponApplied, setCouponApplied] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [order, setOrder] = useState(null);
  const navigate = useNavigate();

  const prepaidOff = SHOW_PREPAID && payStatus.razorpay_enabled && payMethod === "razorpay";
  const discount = couponApplied ? Math.round(subtotal * 0.10) : prepaidOff ? Math.round(subtotal * 0.05) : 0;
  const codFee = !prepaidOff ? COD_FEE * count : 0; // per-bottle COD price — prepaid orders skip it (must match server)
  const payable = subtotal - discount + codFee;
  const phoneOk = /^[6-9]\d{9}$/.test(form.phone.replace(/\s/g, ""));
  const otpNeeded = otpStatus.otp_enabled && !otpVerified;

  useEffect(() => {
    fetch(`${API}/payments/status`).then((r) => r.json()).then(setPayStatus).catch(() => {});
    fetch(`${API}/otp/status`).then((r) => r.json()).then(setOtpStatus).catch(() => {});
  }, []);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setInterval(() => setResendIn((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setState = (e) => setForm((f) => ({ ...f, state: e.target.value, city: "" }));

  /* ---------- Phone OTP ---------- */
  const sendOtp = async () => {
    if (!phoneOk) {
      toast.error("Enter a valid 10-digit Indian mobile number first");
      return;
    }
    setOtpBusy(true);
    try {
      const res = await fetch(`${API}/otp/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: form.phone }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Could not send OTP");
      setOtpSent(true);
      setResendIn(30);
      toast.success("OTP sent to your mobile");
    } catch (err) {
      toast.error(err.message || "Could not send OTP");
    } finally {
      setOtpBusy(false);
    }
  };

  const verifyOtp = async () => {
    setOtpBusy(true);
    try {
      const res = await fetch(`${API}/otp/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: form.phone, code: otpCode }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Incorrect OTP");
      setOtpVerified(true);
      toast.success("Phone verified — genuine orders only");
    } catch (err) {
      toast.error(err.message || "Incorrect OTP");
    } finally {
      setOtpBusy(false);
    }
  };

  const choosePay = (m) => {
    setPayMethod(m);
  };

  const applyCoupon = () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    if (code !== "PREPAID10") {
      toast.error("This coupon doesn't exist. Try PREPAID10.");
      return;
    }
    if (payMethod !== "razorpay") {
      toast.error("PREPAID10 works only on prepaid (online) payments — switch to Pay Online to use it");
      return;
    }
    setCouponApplied(true);
    toast.success("PREPAID10 applied — 10% off");
  };

  const validate = () => {
    if (items.length === 0) {
      toast.error("Your cart is empty");
      return false;
    }
    const required = ["email", "full_name", "phone", "address1", "address2", "state", "city", "zip"];
    for (const k of required) {
      if (!form[k] || !form[k].trim()) {
        toast.error(
          k === "phone"
            ? "Phone number is required for delivery updates"
            : k === "address1"
            ? "Enter your flat / house number and building name"
            : k === "address2"
            ? "Enter your street / area with a nearby landmark"
            : "Please fill in all delivery details"
        );
        return false;
      }
    }
    if (form.address1.trim().length < 3) {
      toast.error("Flat / house number looks too short — the courier needs the full detail");
      return false;
    }
    if (form.address2.trim().length < 8) {
      toast.error("Street / area looks too short — add a nearby landmark so the courier finds you");
      return false;
    }
    if (!/^[1-9][0-9]{5}$/.test(form.zip.trim())) {
      toast.error("Enter a valid 6-digit PIN code");
      return false;
    }
    if (!phoneOk) {
      toast.error("Enter a valid 10-digit Indian mobile number");
      return false;
    }
    if (!form.email.includes("@")) {
      toast.error("Enter a valid email address");
      return false;
    }
    return true;
  };

  const onZip = (e) => {
    const v = e.target.value.replace(/\D/g, "").slice(0, 6);
    setForm((f) => ({ ...f, zip: v }));
    if (/^[1-9][0-9]{5}$/.test(v)) {
      fetch(`${API}/pincode/${v}`)
        .then((r) => r.json())
        .then((d) => {
          if (d.found && d.state && STATES.includes(d.state)) {
            setForm((f) => (f.zip === v ? { ...f, state: d.state, city: CITY_MAP[d.state]?.includes(d.city) ? d.city : f.city } : f));
            setPinDetected({ city: d.city, state: d.state });
            setPinHint(`✓ PIN verified — ${d.city}, ${d.state}`);
          } else if (d.found) {
            setPinDetected({ city: d.city, state: d.state });
            setPinHint(`✓ PIN valid — ${d.city || "your area"}, ${d.state}. Pick your state below if not auto-filled.`);
          } else {
            setPinDetected(null);
            setPinHint("PIN not found in India Post records — please double-check it");
          }
        })
        .catch(() => setPinDetected(null));
    } else {
      setPinHint("");
    }
  };

  useEffect(() => {
    if (!pinDetected?.state || !form.state) return;
    const sel = form.state.trim().toLowerCase();
    const det = pinDetected.state.trim().toLowerCase();
    if (sel && !det.includes(sel) && !sel.includes(det)) {
      setPinHint(
        `⚠ Heads-up: PIN ${form.zip} is in ${pinDetected.city}, ${pinDetected.state} — but you selected ${form.state}. Please double-check.`
      );
    } else {
      setPinHint(`✓ PIN verified — ${pinDetected.city}, ${pinDetected.state}`);
    }
  }, [form.state, pinDetected]);

  const finishOrder = (orderData) => {
    if (window.fbq) {
      window.fbq("track", "Purchase", {
        value: orderData.total,
        currency: "INR",
        num_items: orderData.items.reduce((s, i) => s + i.quantity, 0),
        payment_method: orderData.payment_method,
      });
    }
    setOrder(orderData);
    clear();
    toast.success("Order confirmed — thank you!");
  };

  const openRazorpay = async (orderData) => {
    const res = await fetch(`${API}/payments/create-razorpay-order`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ order_number: orderData.order_number }),
    });
    if (!res.ok) {
      let detail = "";
      try { detail = (await res.json()).detail || ""; } catch { detail = ""; }
      throw new Error(detail || "Payment gateway is not responding right now. Please pay via Cash on Delivery or try again in a minute.");
    }
    let rzp;
    try { rzp = await res.json(); } catch { throw new Error("Could not start payment. Please pay via Cash on Delivery or try again."); }

    await loadRazorpayScript();
    const options = {
      key: rzp.key,
      amount: rzp.amount,
      currency: rzp.currency,
      name: "OmniMist Kitchen Craft",
      description: "OmniMist™ 2-in-1 Glass Oil Sprayer & Dispenser (470ml)",
      order_id: rzp.razorpay_order_id,
      prefill: { name: form.full_name, email: form.email, contact: form.phone },
      theme: { color: "#7C9885" },
      handler: async (response) => {
        try {
          const vRes = await fetch(`${API}/payments/verify`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              order_number: orderData.order_number,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            }),
          });
          if (!vRes.ok) {
            let detail = "";
            try { detail = (await vRes.json()).detail || ""; } catch { detail = ""; }
            throw new Error(detail || "Payment verification failed — if money was deducted, we'll confirm your order shortly.");
          }
          let paid;
          try { paid = await vRes.json(); } catch { throw new Error("Payment received — confirmation is on its way to your inbox."); }
          finishOrder(paid);
        } catch (err) {
          toast.error(err.message || "Payment verification failed");
        } finally {
          setPlacing(false);
        }
      },
      modal: {
        ondismiss: () => {
          setPlacing(false);
          toast.error("Payment cancelled — your order was not placed");
        },
      },
    };
    new window.Razorpay(options).open();
  };

  const placeOrder = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    if (otpNeeded) {
      toast.error("Please verify your phone number with OTP first");
      return;
    }
    setPlacing(true);
    try {
      const res = await fetch(`${API}/orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: items.map((i) => ({ product_id: i.id, quantity: i.qty })),
          customer: { ...form, country: "India" },
          payment_method: payMethod,
          coupon_code: couponApplied ? "PREPAID10" : "",
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Could not place order. Please try again or pay via Cash on Delivery.");

      if (payMethod === "razorpay") {
        await openRazorpay(data);
      } else {
        finishOrder(data);
        setPlacing(false);
      }
    } catch (err) {
      toast.error(err.message || "Could not place order. Please try again.");
      setPlacing(false);
    }
  };

  /* ---------- Success ---------- */
  const celebratedRef = useRef("");
  useEffect(() => {
    // Fire the thank-you sound + confetti exactly once per order.
    if (order && order.order_number && celebratedRef.current !== order.order_number) {
      celebratedRef.current = order.order_number;
      celebrateOrder();
    }
  }, [order]);

  if (order) {
    const paid = order.payment_status === "paid";
    return (
      <div className="flex min-h-screen items-center justify-center bg-oat-100 px-5" data-testid="order-success-view">
        <div className="w-full max-w-lg rounded-3xl border border-charcoal-900/10 bg-white p-10 text-center shadow-xl">
          <span className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-sage-500/15">
            <Check className="h-10 w-10 text-sage-600" strokeWidth={2.4} />
          </span>
          <span className="mt-5 inline-block rounded-full bg-gold/15 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.16em] text-[#8a6a2f]" data-testid="order-success-congrats">
            Congratulations
          </span>
          <h1 className="mt-4 font-serif text-4xl text-charcoal-900">
            {paid ? "Payment received" : "Order confirmed"}
          </h1>
          <p className="mt-3 text-base leading-relaxed text-charcoal-500">
            Thank you, {order.customer.full_name.split(" ")[0]}. Your OmniMist is being
            packed and updates will be sent to{" "}
            <span className="font-medium text-charcoal-900">{order.customer.phone}</span>.
          </p>
          <div className="mt-8 rounded-2xl bg-oat-100 p-6 text-left" data-testid="order-success-summary">
            <div className="flex justify-between text-sm text-charcoal-500">
              <span>Order number</span>
              <span className="font-serif text-lg font-semibold text-charcoal-900" data-testid="order-success-number">
                {order.order_number}
              </span>
            </div>
            <div className="mt-3 flex justify-between text-sm text-charcoal-500">
              <span>{order.items[0].quantity}× OmniMist™ 2-in-1 Sprayer</span>
              <span className="text-charcoal-900">{fmt(order.total)}</span>
            </div>
            {order.discount > 0 && (
              <div className="mt-3 flex justify-between text-sm text-sage-600" data-testid="order-success-discount">
                <span>Prepaid offer (5% off)</span>
                <span>-{fmt(order.discount)}</span>
              </div>
            )}
            <div className="mt-3 flex justify-between text-sm text-charcoal-500">
              <span>Payment</span>
              <span className="font-medium text-charcoal-900" data-testid="order-success-payment">
                {paid
                  ? `Paid via Razorpay · ${order.razorpay_payment_id}`
                  : `Cash on Delivery — pay ${fmt(order.total)}`}
              </span>
            </div>
          </div>
          <button
            onClick={() => navigate("/")}
            data-testid="order-success-back-btn"
            className="mt-8 w-full rounded-full bg-charcoal-900 py-4 text-sm font-medium uppercase tracking-[0.16em] text-oat-100 transition-colors hover:bg-sage-600"
          >
            Back to the Kitchen
          </button>
        </div>
      </div>
    );
  }

  /* ---------- Empty cart ---------- */
  if (count === 0) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-oat-100 px-5" data-testid="checkout-empty-state">
        <span className="flex h-20 w-20 items-center justify-center rounded-full bg-sage-100">
          <ShoppingBag className="h-9 w-9 text-sage-600" strokeWidth={1.4} />
        </span>
        <h1 className="font-serif text-3xl text-charcoal-900">Your cart is empty</h1>
        <p className="max-w-sm text-center text-charcoal-500">
          Add the OmniMist 2-in-1 Glass Oil Sprayer &amp; Dispenser to start cooking
          with less oil and no mess.
        </p>
        <Link
          to="/"
          data-testid="checkout-empty-back-btn"
          className="rounded-full bg-charcoal-900 px-8 py-4 text-sm font-medium uppercase tracking-[0.14em] text-oat-100 transition-colors hover:bg-sage-600"
        >
          Shop OmniMist — {fmt(PRODUCT.price + COD_FEE)}
        </Link>
      </div>
    );
  }

  /* ---------- Checkout ---------- */
  const cities = CITY_MAP[form.state] || [];
  const submitBlocked = otpNeeded;

  return (
    <div className="min-h-screen bg-oat-100" data-testid="checkout-page">
      <header className="border-b border-charcoal-900/10 bg-oat-50/90 backdrop-blur">
        <div className="mx-auto flex h-[72px] max-w-6xl items-center justify-between px-5 lg:px-8">
          <Link to="/" data-testid="checkout-logo-link" aria-label="Back to home">
            <Logo />
          </Link>
          <span className="flex items-center gap-2 rounded-full border border-sage-500/30 bg-sage-100 px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-sage-700">
            <Lock className="h-3.5 w-3.5" /> Secure Checkout
          </span>
        </div>
      </header>

      <main className="mx-auto grid max-w-6xl grid-cols-1 gap-10 px-5 py-12 lg:grid-cols-12 lg:px-8">
        {/* Form */}
        <form onSubmit={placeOrder} className="lg:col-span-7" data-testid="checkout-form">
          <Link
            to="/"
            className="mb-8 inline-flex items-center gap-1.5 text-sm text-charcoal-500 transition-colors hover:text-sage-600"
            data-testid="checkout-back-link"
          >
            <ChevronLeft className="h-4 w-4" /> Continue shopping
          </Link>

          <section className="mb-10">
            <h2 className="mb-5 font-serif text-2xl text-charcoal-900">1 · Contact</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <input
                type="email"
                required
                value={form.email}
                onChange={set("email")}
                placeholder="Email address"
                data-testid="checkout-email-input"
                className={inputCls}
              />
              <div className="flex gap-2">
                <input
                  type="tel"
                  required
                  maxLength={10}
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="Mobile number (10 digits) *"
                  data-testid="checkout-phone-input"
                  className={`${inputCls} min-w-0 flex-1`}
                />
                {otpStatus.otp_enabled && !otpVerified && (
                  <button
                    type="button"
                    onClick={sendOtp}
                    disabled={otpBusy || !phoneOk || (otpSent && resendIn > 0)}
                    data-testid="otp-send-btn"
                    className="shrink-0 rounded-xl bg-charcoal-900 px-4 py-3 text-xs font-semibold uppercase tracking-[0.1em] text-oat-100 transition-colors hover:bg-sage-600 disabled:opacity-50"
                  >
                    {otpBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : otpSent && resendIn > 0 ? `Resend ${resendIn}s` : otpSent ? "Resend" : "Send OTP"}
                  </button>
                )}
              </div>
            </div>

            {/* OTP verify row */}
            {otpStatus.otp_enabled && otpSent && !otpVerified && (
              <div className="mt-4 flex flex-wrap items-center gap-2 rounded-2xl border border-charcoal-900/15 bg-white p-4" data-testid="otp-verify-box">
                <MessageSquareText className="h-5 w-5 shrink-0 text-sage-600" />
                <input
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="Enter 6-digit OTP"
                  inputMode="numeric"
                  data-testid="otp-input"
                  className="min-w-0 flex-1 rounded-xl border border-charcoal-900/15 px-4 py-3 text-center font-mono text-lg tracking-[0.4em] text-charcoal-900 focus:border-sage-500 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={verifyOtp}
                  disabled={otpBusy || otpCode.length !== 6}
                  data-testid="otp-verify-btn"
                  className="shrink-0 rounded-xl bg-sage-500 px-5 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-white transition-colors hover:bg-sage-600 disabled:opacity-50"
                >
                  {otpBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}
                </button>
              </div>
            )}

            {otpStatus.otp_enabled && otpVerified && (
              <p className="mt-3 flex items-center gap-1.5 rounded-xl bg-sage-100 px-4 py-2.5 text-sm font-medium text-sage-700" data-testid="otp-verified-chip">
                <BadgeCheck className="h-4 w-4" /> Phone verified — genuine orders only
              </p>
            )}
            <p className="mt-2 text-xs text-charcoal-500">
              {otpStatus.otp_enabled
                ? "We verify every number with a one-time password — no fake orders."
                : "Phone is needed — the courier calls this number before delivery."}
            </p>
          </section>

          <section className="mb-10">
            <h2 className="mb-5 font-serif text-2xl text-charcoal-900">2 · Delivery</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <input
                required
                value={form.full_name}
                onChange={set("full_name")}
                placeholder="Full name"
                data-testid="checkout-name-input"
                className={`${inputCls} sm:col-span-2`}
              />
              <input
                required
                value={form.address1}
                onChange={set("address1")}
                placeholder="Flat / House No., Floor, Building / Apartment name *"
                data-testid="checkout-address1-input"
                className={`${inputCls} sm:col-span-2`}
              />
              <input
                required
                value={form.address2}
                onChange={set("address2")}
                placeholder="Street / Area / Colony + nearby landmark *"
                data-testid="checkout-address2-input"
                className={`${inputCls} sm:col-span-2`}
              />
              <select
                required
                value={form.state}
                onChange={setState}
                data-testid="checkout-state-select"
                className={selectCls}
              >
                <option value="" disabled>
                  Select state
                </option>
                {STATES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <select
                required
                value={form.city}
                onChange={set("city")}
                disabled={!form.state}
                data-testid="checkout-city-select"
                className={`${selectCls} disabled:opacity-50`}
              >
                <option value="" disabled>
                  {form.state ? "Select city" : "Select state first"}
                </option>
                {cities.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <div className="sm:col-span-2">
                <input
                  required
                  value={form.zip}
                  onChange={onZip}
                  placeholder="6-digit PIN code (city & state auto-fill)"
                  data-testid="checkout-zip-input"
                  className={inputCls}
                  inputMode="numeric"
                />
                {pinHint && (
                  <p
                    className={`mt-1.5 px-1 text-xs ${
                      pinHint.startsWith("✓")
                        ? "text-sage-700"
                        : pinHint.startsWith("⚠")
                        ? "text-[#8a6a2f]"
                        : "text-[#b91c1c]"
                    }`}
                    data-testid="checkout-pin-hint"
                  >
                    {pinHint}
                  </p>
                )}
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="mb-5 font-serif text-2xl text-charcoal-900">3 · Payment</h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => choosePay("cod")}
                data-testid="pay-cod-option"
                className={`flex items-start gap-3 rounded-2xl border p-5 text-left transition-all duration-300 ${
                  payMethod === "cod"
                    ? "border-sage-500 bg-sage-100/60 ring-2 ring-sage-500/20"
                    : "border-charcoal-900/15 bg-white hover:border-sage-400"
                }`}
              >
                <Banknote className={`mt-0.5 h-6 w-6 ${payMethod === "cod" ? "text-sage-600" : "text-charcoal-500"}`} />
                <span>
                  <span className="block font-medium text-charcoal-900">Cash on Delivery</span>
                  <span className="mt-1 block text-xs leading-relaxed text-charcoal-500">
                    Pay {fmt(subtotal + COD_FEE * count)} in cash or UPI when the bottle arrives. We
                    confirm every order on WhatsApp before dispatching.
                  </span>
                  {SHOW_PREPAID && payStatus.razorpay_enabled && payMethod === "cod" && (
                    <span
                      className="mt-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.1em] text-sage-700 animate-pulse"
                      data-testid="cod-switch-nudge"
                    >
                      <Zap className="h-3.5 w-3.5" />
                      Use Prepaid — pay only {fmt(subtotal - Math.round(subtotal * 0.05))} instead of{" "}
                      {fmt(subtotal + COD_FEE * count)} and save {fmt(COD_FEE * count + Math.round(subtotal * 0.05))}
                    </span>
                  )}
                </span>
              </button>
              {SHOW_PREPAID && payStatus.razorpay_enabled && (
                <button
                  type="button"
                  onClick={() => choosePay("razorpay")}
                  data-testid="pay-razorpay-option"
                  className={`relative flex items-start gap-3 rounded-2xl border p-5 text-left transition-all duration-300 ${
                    payMethod === "razorpay"
                      ? "border-sage-500 bg-sage-100/60 ring-2 ring-sage-500/20"
                      : "border-charcoal-900/15 bg-white hover:border-sage-400"
                  }`}
                >
                  <CreditCard className={`mt-0.5 h-6 w-6 ${payMethod === "razorpay" ? "text-sage-600" : "text-charcoal-500"}`} />
                  <span>
                  <span className="flex flex-wrap items-center gap-2 font-medium text-charcoal-900">
                      Pay Online
                      <span
                        className="rounded bg-gold/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-[#8a6a2f] animate-pulse"
                        data-testid="prepaid-limited-badge"
                      >
                        Limited time
                      </span>
                      {prepaidOff && (
                        <span className="rounded bg-sage-600 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white" data-testid="prepaid-badge">
                          5% off applied
                        </span>
                      )}
                      <span className="rounded bg-[#0C2451] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">
                        Razorpay
                      </span>
                    </span>
                    <span className="mt-1 block text-xs leading-relaxed text-charcoal-500">
                      UPI · Cards · NetBanking · Wallets — 100% secure. Pay{" "}
                      <strong className="text-sage-700">{fmt(subtotal - Math.round(subtotal * 0.05))}</strong> instead
                      of the COD price {fmt(subtotal + COD_FEE * count)}.
                    </span>
                    {prepaidOff && (
                      <span className="mt-1 block text-xs font-semibold text-sage-700" data-testid="prepaid-savings-line">
                        You save {fmt(discount + COD_FEE * count)} vs COD — prepaid offer applied automatically.
                      </span>
                    )}
                  </span>
                </button>
              )}
            </div>

            {SHOW_PREPAID && SHOW_COUPON && (
              <div className="mt-5 rounded-2xl border border-charcoal-900/15 bg-white p-4" data-testid="coupon-box">
                {couponApplied ? (
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-sm font-medium text-sage-700">
                      <TicketPercent className="h-4 w-4" />
                      PREPAID10 applied — you save {fmt(discount)}
                    </span>
                    <button
                      type="button"
                      onClick={() => setCouponApplied(false)}
                      data-testid="coupon-remove-btn"
                      aria-label="Remove coupon"
                      className="flex h-7 w-7 items-center justify-center rounded-full border border-charcoal-900/15 text-charcoal-500 hover:text-red-500"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      value={couponInput}
                      onChange={(e) => setCouponInput(e.target.value)}
                      placeholder="Coupon code — try PREPAID10"
                      data-testid="coupon-input"
                      className={`${inputCls} uppercase`}
                    />
                    <button
                      type="button"
                      onClick={applyCoupon}
                      data-testid="coupon-apply-btn"
                      className="shrink-0 rounded-xl bg-charcoal-900 px-5 py-3 text-xs font-medium uppercase tracking-[0.14em] text-oat-100 transition-colors hover:bg-sage-600"
                    >
                      Apply
                    </button>
                  </div>
                )}
                {!couponApplied && (
                  <p className="mt-2 text-xs text-charcoal-500">
                    Extra 10% off on prepaid (online) orders only — not on Cash on
                    Delivery.
                  </p>
                )}
              </div>
            )}

            {payMethod === "cod" && (
              <p className="mt-5 rounded-xl bg-sage-100 px-4 py-3 text-sm text-sage-700" data-testid="cod-notice">
                Keep {fmt(subtotal + COD_FEE * count)} ready at the door. Our courier also accepts UPI on
                delivery.
              </p>
            )}
          </section>

          {submitBlocked && (
            <p className="mb-4 rounded-xl border border-gold/40 bg-gold/10 px-4 py-3 text-center text-sm font-medium text-[#8a6a2f]" data-testid="otp-required-notice">
              Verify your phone with OTP to place the order — it takes 10 seconds.
            </p>
          )}

          <button
            type="submit"
            disabled={placing || submitBlocked}
            data-testid="checkout-submit-order-btn"
            className="flex w-full items-center justify-center gap-3 rounded-full bg-charcoal-900 py-4 text-sm font-medium uppercase tracking-[0.16em] text-oat-100 transition-all duration-300 hover:bg-sage-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {placing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Processing…
              </>
            ) : payMethod === "razorpay" ? (
              `Pay Online · ${fmt(payable)}`
            ) : (
              `Place Order · Pay ${fmt(payable)} on Delivery`
            )}
          </button>

          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-charcoal-500">
            <span className="flex items-center gap-1.5">
              <Lock className="h-3.5 w-3.5 text-sage-600" /> SSL 256-bit encrypted
            </span>
            <span className="flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 text-sage-600" /> Cash on Delivery available
            </span>
            <span className="flex items-center gap-1.5">
              <Truck className="h-3.5 w-3.5 text-sage-600" /> Free express shipping
              <span className="ml-1 font-normal text-charcoal-500" data-testid="summary-delivery-estimate">
                · ships today, delivery by{" "}
                {new Date(Date.now() + 4 * 864e5).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })}
              </span>
            </span>
          </div>
        </form>

        {/* Summary */}
        <aside className="lg:col-span-5">
          <div
            className="rounded-3xl border border-charcoal-900/10 bg-white p-7 lg:sticky lg:top-8"
            data-testid="checkout-order-summary"
          >
            <h2 className="mb-6 font-serif text-2xl text-charcoal-900">Order summary</h2>
            {items.map((i) => (
              <div key={i.id} className="mb-5 flex gap-4" data-testid="summary-item">
                <img src={IMAGES.studio} alt={i.name} className="h-20 w-20 rounded-2xl object-cover" />
                <div className="flex-1">
                  <p className="font-medium text-charcoal-900">{i.name}</p>
                  <p className="mt-0.5 text-xs uppercase tracking-[0.14em] text-charcoal-500">
                    {i.capacity}
                  </p>
                  <div className="mt-2.5 flex items-center gap-3">
                    <div className="flex items-center gap-3 rounded-full border border-charcoal-900/15 px-3 py-1.5">
                      <button
                        type="button"
                        onClick={() => setQty(i.id, i.qty - 1)}
                        data-testid="summary-qty-minus"
                        aria-label="Decrease quantity"
                      >
                        <Minus className="h-3 w-3" />
                      </button>
                      <span className="min-w-4 text-center text-sm" data-testid="summary-qty">
                        {i.qty}
                      </span>
                      <button
                        type="button"
                        onClick={() => setQty(i.id, i.qty + 1)}
                        data-testid="summary-qty-plus"
                        aria-label="Increase quantity"
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                    </div>
                    <span className="text-sm text-charcoal-500">× {fmt(comboUnitPrice(i.qty))}</span>
                  </div>
                </div>
              </div>
            ))}
            <div className="space-y-3 border-t border-charcoal-900/10 pt-5 text-sm">
              <div className="flex justify-between text-charcoal-500">
                <span>Subtotal</span>
                <span data-testid="summary-subtotal">{fmt(subtotal)}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between font-medium text-sage-600" data-testid="summary-discount">
                  <span>{couponApplied ? "PREPAID10 (10% off)" : "Prepaid offer (5% off)"}</span>
                  <span>-{fmt(discount)}</span>
                </div>
              )}
              <div className="flex justify-between text-charcoal-500">
                <span>Shipping</span>
                <span className="font-medium text-sage-600" data-testid="summary-shipping">
                  FREE
                </span>
              </div>
              <div className="flex justify-between border-t border-charcoal-900/10 pt-4">
                <span className="text-base uppercase tracking-[0.16em] text-charcoal-500">
                  Total
                </span>
                <span className="font-serif text-2xl font-semibold text-charcoal-900" data-testid="summary-total">
                  {fmt(payable)}
                </span>
              </div>
            </div>
          </div>
        </aside>
      </main>
    </div>
  );
}
